import React, { useState, useEffect } from "react";
import axios from "axios";
import SEO from '../SEO/SEO';
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import KekaJobsEmbed from "./KekaJobsEmbed";

const Career = () => {
  const [banner, setBanner] = useState(null);
  const [bannerLoading, setBannerLoading] = useState(true);

  // Fetch active banner
  useEffect(() => {
    const fetchBanner = async () => {
      try {
        setBannerLoading(true);
        const response = await axios.get('/api/banners/active?page_type=career');
        if (response.data.success && response.data.data.length > 0) {
          setBanner(response.data.data[0]);
        }
      } catch (error) {
        console.error('Error fetching career banner:', error);
      } finally {
        setBannerLoading(false);
      }
    };
    fetchBanner();
  }, []);

  return (
    <div className="min-h-screen bg-[#f5f5f5] font-[Roboto,system-ui,-apple-system,sans-serif]">

      <SEO
        title="Careers at Aham Housing Finance | Jobs, Growth & Opportunities"
        description="Explore career opportunities at Aham Housing Finance. Join a growing team and build a rewarding future. Apply today!"
        keywords="Aham Housing Finance career, Aham career, Aham Housing Finance jobs, careers at Aham, Aham vacancies, work at Aham Housing Finance, housing finance jobs, NBFC jobs India, financial services careers, employee growth Aham, merit based career growth, team culture Aham, performance incentives jobs, branch jobs Aham Housing Finance, sales jobs housing finance, credit jobs housing finance, operations jobs NBFC, join Aham Housing Finance, current openings Aham, career opportunities Aham"
        canonical="https://ahamhfc.com/career"
        ogTitle="Careers at Aham Housing Finance | Jobs, Growth & Opportunities"
        ogDescription="Explore career opportunities at Aham Housing Finance. Build your future with a team that values growth, performance, learning, and meaningful work across branches and functions."
      />

      {/* Banner Section */}
      <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('${banner && banner.image_url ? (banner.image_url.startsWith('http') ? banner.image_url : banner.image_url.startsWith('/') ? banner.image_url : `/${banner.image_url}`) : 'img/Banners/Career-Banner.png'}')`,
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-primary-900/95 via-primary-800/80 to-transparent"></div>
        </div>

        {/* Content */}
        <div className="relative z-10 container mx-auto px-4 py-12 md:py-20 text-left">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-3xl md:text-6xl font-extrabold text-white mb-6 leading-tight">
              {banner?.title || "Find Your Dream Job"}
            </h1>
            <p className="text-lg md:text-3xl mb-8 text-white/90 font-medium max-w-4xl leading-relaxed">
              {banner?.subtitle || "Explore exciting opportunities to grow with our innovative team."}
            </p>

            {/* Dynamic Buttons or Default */}
            <div className="flex flex-wrap gap-4 mb-8">
              {banner?.primary_button_text ? (
                <motion.a
                  href={banner.primary_button_link}
                  className="bg-yellow-400 hover:bg-yellow-500 text-primary-900 px-8 py-3 rounded-full font-bold text-lg transition-colors duration-200 shadow-lg"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {banner.primary_button_text}
                </motion.a>
              ) : (
                <motion.a
                  href="#openings"
                  className="bg-yellow-400 hover:bg-yellow-500 text-primary-900 px-8 py-3 rounded-full font-bold text-lg transition-colors duration-200 shadow-lg"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Explore Openings
                </motion.a>
              )}

              {banner?.secondary_button_text && (
                <motion.a
                  href={banner.secondary_button_link}
                  className="bg-white/10 hover:bg-white/20 text-white border border-white/30 px-8 py-3 rounded-full font-bold text-lg backdrop-blur-sm transition-colors duration-200"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {banner.secondary_button_text}
                </motion.a>
              )}
            </div>

            {/* Breadcrumb Navigation */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1, duration: 0.6 }}
              className="flex items-center space-x-2"
            >
              <Link
                to="/"
                className="text-white/80 hover:text-white transition-colors duration-200 text-sm font-medium"
              >
                Home
              </Link>
              <ChevronRightIcon className="h-4 w-4 text-white/60" />
              <span className="text-yellow-300 text-sm font-semibold">Career</span>
            </motion.div>

          </motion.div>
        </div>
      </section>

      {/* Career Portal Section */}
      <section id="openings" className="py-12 md:py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-8 md:mb-12">
            <h2 className="text-3xl md:text-4xl font-extrabold text-primary-900 mb-4 tracking-tight">Current Openings</h2>
            <p className="text-lg text-gray-600 leading-relaxed">
              Join us in our mission to make home ownership accessible. Browse through our current job opportunities
              and find your perfect fit.
            </p>
          </div>

          <div className="max-w-5xl mx-auto">
            <KekaJobsEmbed />
          </div>
        </div>
      </section>

      {/* Why Join Us Section - Optional addition for better UX */}
      {/* <section className="py-20 bg-primary-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-primary-100">
              <div className="w-12 h-12 bg-primary-600 rounded-xl flex items-center justify-center mb-6 text-white text-2xl">🤝</div>
              <h3 className="text-xl font-bold text-primary-900 mb-3">Great Culture</h3>
              <p className="text-gray-600">Work in an environment that values collaboration, innovation, and mutual respect.</p>
            </div>
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-primary-100">
              <div className="w-12 h-12 bg-primary-600 rounded-xl flex items-center justify-center mb-6 text-white text-2xl">📈</div>
              <h3 className="text-xl font-bold text-primary-900 mb-3">Growth Opportunities</h3>
              <p className="text-gray-600">Accelerate your career with continuous learning and clear career paths.</p>
            </div>
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-primary-100">
              <div className="w-12 h-12 bg-primary-600 rounded-xl flex items-center justify-center mb-6 text-white text-2xl">⭐️</div>
              <h3 className="text-xl font-bold text-primary-900 mb-3">Competitive Benefits</h3>
              <p className="text-gray-600">We offer attractive compensation and benefits packages to our employees.</p>
            </div>
          </div>
        </div>
      </section> */}
    </div>
  );
};

export default Career;